/* eslint-disable import/prefer-default-export */
import { ref } from 'vue';
import { defineStore } from 'pinia';
import useLocalStorage from '@/composables/storage/useLocalStorage';
import filters from '@/composables/store/filters';
import useFormValidation from '@/composables/validations/useFormValidation';
import useGlobalActions from '@/composables/store/globalActions';
// eslint-disable-next-line import/no-named-as-default

const { bindLocalStorage } = useLocalStorage();
const {
  validateFullNameField,
  validateDobField,
  validateCheckboxField,
  validateAddressLine1Field,
  validateRequiredField,
} = useFormValidation();
const { formHasErrors, getErrorByField } = filters();
const { updateErrorFieldByKey } = useGlobalActions();

const formObject = ref({
  fullName: {
    value: null,
    error: null,
    required: true,
    validator: validateFullNameField,
  },
  dob: {
    value: null,
    error: null,
    required: true,
    validator: validateDobField,
  },
  addressLine1: {
    value: null,
    required: true,
    error: null,
    validator: validateAddressLine1Field,
  },
  suburb: {
    value: null,
    error: null,
    required: true,
    validator: validateRequiredField,
  },
  state: {
    value: null,
    error: null,
    required: true,
    validator: validateRequiredField,
  },
  postcode: {
    value: null,
    error: null,
    required: true,
    validator: validateRequiredField,
  },
  country: {
    value: null,
    error: null,
    required: true,
    validator: validateRequiredField,
  },
  zerohash_tnc: {
    value: false,
    required: false,
    error: null,
    validator: validateCheckboxField,
  },
});

export const useIdentityConfirmationStore = defineStore({
  id: 'identityConfirmation',
  state: () => ({
    form: formObject,
    personalDetails: {},
    /** Optional: (field) => boolean. When set, updateValue only runs validator when true. */
    relevanceChecker: null,
  }),
  getters: {
    formHasErrors: (store) => formHasErrors(store),
    getErrorByField: (store) => (field) => getErrorByField(store)(field),
    getPersonalDetails: (store) => store.personalDetails,
    getCountry: (store) => store.form.country.value,
    getGivenName: (store) => store.form.fullName.value?.split(' ')[0] || '',
  },
  actions: {
    setRelevanceChecker(fn) {
      this.relevanceChecker = typeof fn === 'function' ? fn : null;
    },
    updateValue(field, val) {
      if (this.relevanceChecker && !this.relevanceChecker(field)) {
        this.form[field].value = val;
        if (Object.getOwnPropertyDescriptor(this.form[field], 'error')) {
          this.form[field].error = '';
        }
        bindLocalStorage(this.$id, this.form);
        return;
      }
      if (Object.getOwnPropertyDescriptor(this.form[field], 'validator')) {
        const { validator } = this.form[field];

        this.form[field].error = validator(field, val);
      }
      this.form[field].value = val;
      bindLocalStorage(this.$id, this.form);
    },
    updateRequiredFieldForKey(field, isRequired) {
      if (
        !field
        || !Object.getOwnPropertyDescriptor(this.form, field)
        || !Object.getOwnPropertyDescriptor(this.form[field], 'required')
      ) {
        return;
      }

      this.form[field].required = isRequired;
    },
    setPersonalDetails(personalDetails) {
      this.personalDetails = personalDetails;
    },
    updateErrorFieldByKey(field, error) {
      updateErrorFieldByKey(this.form, field, error);
    },
    validateField(field) {
      const formField = this.form[field];
      if (!formField) return;
      if (Object.getOwnPropertyDescriptor(formField, 'validator')) {
        formField.error = formField.validator(field, formField.value);
      }
    },
    validateAllFields() {
      Object.keys(this.form).forEach((field) => {
        this.validateField(field);
      });
      return !this.formHasErrors;
    },
  },
});
